import axios from './axios';

const BASE = '/quotations';

/** Build "?sizes=22,24,26" suffix from an array, or "" when empty/null. */
function qsSizes(sizesArr) {
  if (!Array.isArray(sizesArr) || sizesArr.length === 0) return "";
  const csv = sizesArr.map(s => String(s).trim()).filter(Boolean).join(",");
  if (!csv) return "";
  return "?sizes=" + encodeURIComponent(csv);
}

/**
 * Phase 36da.99.ei — build the size-filter query string supporting BOTH the
 * legacy global `sizes` CSV and the new per-item `itemSizes` map.
 * itemSizesMap shape: { [quotationItemId]: ["22","24",...] }.  When the map is
 * present and non-empty it drives per-item filtering; otherwise falls back to
 * the global sizesArr (or no filter = all sizes).
 */
function qsSizeFilter(sizesArr, itemSizesMap) {
  const parts = [];
  if (Array.isArray(sizesArr) && sizesArr.length) {
    const csv = sizesArr.map(s => String(s).trim()).filter(Boolean).join(",");
    if (csv) parts.push("sizes=" + encodeURIComponent(csv));
  }
  if (itemSizesMap && typeof itemSizesMap === "object" && Object.keys(itemSizesMap).length) {
    parts.push("itemSizes=" + encodeURIComponent(JSON.stringify(itemSizesMap)));
  }
  return parts.length ? "?" + parts.join("&") : "";
}

// Phase 36da.99.qcol — append showImage / showBarcode flags to a query string
// that already came back from qsSizeFilter (may be "" or "?...").  Only emits a
// flag when it is explicitly false (true is the server default).
function qsCols(prefix, cols) {
  if (!cols) return prefix;
  const extra = [];
  if (cols.showImage === false) extra.push("showImage=false");
  if (cols.showBarcode === false) extra.push("showBarcode=false");
  if (!extra.length) return prefix;
  return prefix + (prefix.startsWith("?") ? "&" : "?") + extra.join("&");
}

export const quotationApi = {
  getAll:          ()         => axios.get(BASE),
  // ALL quotations for ONE customer — the Sale Order form.
  //
  // getAll transfers every quotation in the COMPANY; this narrows it to the
  // customer in the database. NOT filtered by status: the page filters to
  // ACCEPTED for the picker itself, but also needs the other statuses for
  // its counts and to resolve a line's source quotation when pulling rates.
  forCustomer:     (customerId) => axios.get(`${BASE}/for-customer/${customerId}`),
  /** Phase 5 — server-paged + sortable register listing.
   *  params: { page, size, sortBy, sortDir, q, status, customerId } */
  paged:           (params)   => axios.get(`${BASE}/paged`, { params }),
  /** Phase 5 — paged barcode register: one row per QuotationItem.
   *  params: { page, size, sortBy, sortDir, q, status, customerId, patternId, fromDate, toDate } */
  barcodeRegisterPaged: (params) => axios.get(`${BASE}/barcode-register/paged`, { params }),
  nextNumber:      ()         => axios.get(`${BASE}/next-number`),
  getById:         (id)       => axios.get(`${BASE}/${id}`),
  create:          (data)     => axios.post(BASE, data),
  update:          (id, data) => axios.put(`${BASE}/${id}`, data),
  // Phase 36da.99.do — limited "Edit Info" for ACCEPTED (or any) quotations.
  updateMeta:      (id, data) => axios.patch(`${BASE}/${id}/update-meta`, data),
  send:            (id)       => axios.post(`${BASE}/${id}/send`),
  accept:          (id)       => axios.post(`${BASE}/${id}/accept`),
  acceptRevision:  (id, revNum) => axios.post(`${BASE}/${id}/accept-revision?revisionNumber=${revNum}`),
  reject:          (id)       => axios.post(`${BASE}/${id}/reject`),
  newRevision:     (id)       => axios.post(`${BASE}/${id}/new-revision`),
  convertToSO:     (id, data) => axios.post(`${BASE}/${id}/convert-to-so`, data),
  convertToSample: (id, data) => axios.post(`${BASE}/${id}/convert-to-sample`, data),
  /**
   * Phase 36i — sizesArr is an optional array of size value strings to
   * include in the PDF.  When omitted/empty, all sizes are shown (legacy
   * behaviour).  Example: ["22","24","26"] → only those columns.
   */
  downloadPdf:              (id, sizesArr, itemSizesMap, cols)           => axios.get(`${BASE}/${id}/pdf${qsCols(qsSizeFilter(sizesArr, itemSizesMap), cols)}`, { responseType: "arraybuffer" }),
  downloadPdfWithPriceGroup:(id, pgId, sizesArr, itemSizesMap)     => axios.get(`${BASE}/${id}/pdf/price-group/${pgId}${qsSizeFilter(sizesArr, itemSizesMap)}`, { responseType: "arraybuffer" }),
  downloadPartnerPriceList:(id, partnerId)           => axios.get(`${BASE}/${id}/partner-price-list/${partnerId}`, { responseType: "arraybuffer" }),
  downloadPdfWithLevel:     (id, pgId, levelId, sizesArr, itemSizesMap, cols) => axios.get(`${BASE}/${id}/pdf/price-group/${pgId}/level/${levelId}${qsCols(qsSizeFilter(sizesArr, itemSizesMap), cols)}`, { responseType: "arraybuffer" }),
  // Phase 36da.99.vp — "View Prices" matrix: every level's per-size final
  // rate, computed server-side by the SAME service the PDF uses, against the
  // quotation's accepted-version snapshot.  pgId optional (defaults to the
  // quotation's own price group).
  levelPrices:              (id, pgId) => axios.get(`${BASE}/${id}/level-prices`, { params: pgId ? { priceGroupId: pgId } : {} }),
  // Phase 36da.99.l — Excel siblings, identical signatures to the PDF ones
  downloadExcel:              (id, sizesArr, itemSizesMap)           => axios.get(`${BASE}/${id}/excel${qsSizeFilter(sizesArr, itemSizesMap)}`, { responseType: "arraybuffer" }),
  downloadExcelWithPriceGroup:(id, pgId, sizesArr, itemSizesMap)     => axios.get(`${BASE}/${id}/excel/price-group/${pgId}${qsSizeFilter(sizesArr, itemSizesMap)}`, { responseType: "arraybuffer" }),
  downloadExcelWithLevel:     (id, pgId, levelId, sizesArr, itemSizesMap) => axios.get(`${BASE}/${id}/excel/price-group/${pgId}/level/${levelId}${qsSizeFilter(sizesArr, itemSizesMap)}`, { responseType: "arraybuffer" }),
  delete:          (id)       => axios.delete(`${BASE}/${id}`),
  // Phase 36n.4a — rebuild rate_snapshot for one quotation item from
  // current pattern/cloth/matching/service master data.  Does NOT change
  // the saved per-size rates.
  refreshItemSnapshot: (id, itemId) => axios.post(`${BASE}/${id}/items/${itemId}/refresh-snapshot`),
  // ── Phase 36bd — Per-line (per-barcode) photo + tech pack ─────────────
  // These hit the new /api/quotation-items/{id}/... endpoints exposed by
  // QuotationItemFileController.  They operate on a QuotationItem id (the
  // `itemId` returned in BarcodeQuotationResponse), NOT on a quotation id.
  uploadItemImage: (itemId, file) => {
    const fd = new FormData(); fd.append("file", file);
    return axios.post(`/quotation-items/${itemId}/image`, fd, {
      headers: { "Content-Type": undefined }
    });
  },
  deleteItemImage: (itemId) => axios.delete(`/quotation-items/${itemId}/image`),
  uploadItemTechPack: (itemId, file) => {
    const fd = new FormData(); fd.append("file", file);
    return axios.post(`/quotation-items/${itemId}/tech-pack`, fd, {
      headers: { "Content-Type": undefined }
    });
  },
  deleteItemTechPack: (itemId) => axios.delete(`/quotation-items/${itemId}/tech-pack`),
  downloadItemTechPack: (itemId) =>
    axios.get(`/quotation-items/${itemId}/tech-pack`, { responseType: "arraybuffer" }),
};